import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { FILTER_KEYS, SECTION_LABEL, cleanParams } from "@/lib/analytics";
import { PageHeader } from "@/components/common/ui-kit";
import { FilterBar } from "@/components/analytics/FilterBar";
import { Attention, DataTable, KpiStrip } from "@/components/analytics/SectionView";
import { DrillSheet, ExportButtons } from "@/components/analytics/DrillSheet";
import { CustomerSnapshot } from "@/components/analytics/CustomerSnapshot";

export default function AnalyticsPage() {
  const [sp, setSp] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [drill, setDrill] = useState(null);
  const tab = sp.get("tab") || "overview";
  const customer = sp.get("customer");
  const f = useMemo(() => Object.fromEntries(FILTER_KEYS.map((k) => [k, sp.get(k) || ""])), [sp]);
  const key = FILTER_KEYS.map((k) => f[k]).join("|");
  const upd = (ch) => { const n = new URLSearchParams(sp); Object.entries(ch).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k))); setSp(n, { replace: true }); };

  useEffect(() => { api.get("/analytics/meta").then((r) => setMeta(r.data)).catch(() => setMeta({ sections: [], options: {} })); }, []);
  useEffect(() => {
    setData(null);
    api.get(`/analytics/section/${tab}`, { params: cleanParams({ ...f, period: f.period || "month" }) }).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [tab, key]); // eslint-disable-line react-hooks/exhaustive-deps

  const params = cleanParams({ ...f, period: f.period || "month" });
  return (
    <div data-testid="analytics-page">
      <PageHeader overline="Direzione" title="Analytics" testId="analytics-header"
        description="Vedere, capire, decidere, agire: ogni numero apre i record che lo compongono, nel perimetro del tuo ruolo."
        actions={meta?.can_export && <ExportButtons params={{ ...params, section: tab }} label={tab} testId="section-export" />} />
      <FilterBar f={f} set={(k, v) => upd({ [k]: v })} reset={() => upd(Object.fromEntries(FILTER_KEYS.filter((k) => k !== "period").map((k) => [k, ""])))} options={meta?.options} />
      <div className="-mx-1 mb-6 flex gap-1 overflow-x-auto px-1 pb-1" role="tablist" data-testid="analytics-tabs">
        {(meta?.sections || []).map((s) => <button key={s} type="button" role="tab" aria-selected={tab === s} onClick={() => upd({ tab: s, customer: "" })} data-testid={`analytics-tab-${s}`}
          className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors duration-150 ${tab === s ? "bg-ink text-white" : "text-slate-600 hover:bg-slate-100"}`}>{SECTION_LABEL[s]}</button>)}
      </div>
      {data?.error && <p className="text-sm text-slate-500" data-testid="analytics-error">Sezione non disponibile per il tuo ruolo.</p>}
      {data && !data.error && <div className="space-y-6 animate-rise" data-testid={`analytics-section-${tab}`}>
        {customer && <CustomerSnapshot cid={customer} filters={params} onDrill={setDrill} onClose={() => upd({ customer: "" })} />}
        {!!data.kpis.length && <KpiStrip kpis={data.kpis} onDrill={setDrill} />}
        {data.attention && <Attention items={data.attention} onDrill={setDrill} />}
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">{data.tables.map((t) => <div key={t.key} className={t.columns.length > 5 || t.chart === "trend" ? "xl:col-span-2" : ""}>
          <DataTable t={t} onDrill={setDrill} onCustomer={(c) => { upd({ customer: c }); window.scrollTo({ top: 0, behavior: "smooth" }); }} /></div>)}</div>
      </div>}
      <DrillSheet drill={drill} filters={params} canExport={meta?.can_export} onClose={() => setDrill(null)} />
    </div>
  );
}
